import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PixelConfig {
  pixelId: string | null;
  ga4MeasurementId: string | null;
  clientIp: string | null;
  clientIpV6: string | null;
  clientIpV4: string | null;
  countryCode: string | null;
  geoCity: string | null;
  geoRegion: string | null;
  geoPostal: string | null;
  isVPN: boolean;
  isProxy: boolean;
  isTor: boolean;
  isHosting: boolean;
  isAbuser: boolean;
  isAnonymous: boolean;
  isBogon: boolean;
  isICloudRelay: boolean;
  callingCode: string | null;
  asnType: string | null;
  asnName: string | null;
}

declare global {
  interface Window {
    __PIXEL_CONFIG__?: PixelConfig & { _ts?: number; [key: string]: unknown };
    __FB_PRE_INITIALIZED__?: boolean;
    __GA4_PRE_INITIALIZED__?: boolean;
  }
}

const LS_CACHE_KEY = '_pixel_config_cache';
const CACHE_TTL = 3600000; // 1 hour

const EMPTY_CONFIG: PixelConfig = {
  pixelId: null, ga4MeasurementId: null, clientIp: null, clientIpV6: null, clientIpV4: null,
  countryCode: null, geoCity: null, geoRegion: null, geoPostal: null,
  isVPN: false, isProxy: false, isTor: false, isHosting: false,
  isAbuser: false, isAnonymous: false, isBogon: false, isICloudRelay: false,
  callingCode: null, asnType: null, asnName: null,
};

// Module-level cache so both FacebookPixel and GoogleAnalytics share one call
let cachedConfig: PixelConfig | null = null;
let fetchPromise: Promise<PixelConfig> | null = null;

// Check if we have a pre-loaded config from index.html inline script or localStorage
function getPreloadedConfig(): PixelConfig | null {
  // Check window.__PIXEL_CONFIG__ (set by index.html inline script from localStorage)
  if (window.__PIXEL_CONFIG__?.pixelId) {
    const cfg = window.__PIXEL_CONFIG__ as Record<string, unknown>;
    return {
      ...EMPTY_CONFIG,
      pixelId: window.__PIXEL_CONFIG__.pixelId,
      ga4MeasurementId: window.__PIXEL_CONFIG__.ga4MeasurementId || null,
      clientIp: window.__PIXEL_CONFIG__.clientIp || null,
      clientIpV6: window.__PIXEL_CONFIG__.clientIpV6 || null,
      clientIpV4: window.__PIXEL_CONFIG__.clientIpV4 || null,
      countryCode: cfg.countryCode as string || null,
      geoCity: cfg.geoCity as string || null,
      geoRegion: cfg.geoRegion as string || null,
      geoPostal: cfg.geoPostal as string || null,
      isVPN: !!cfg.isVPN,
      isProxy: !!cfg.isProxy,
      isTor: !!cfg.isTor,
      isHosting: !!cfg.isHosting,
      isAbuser: !!cfg.isAbuser,
      isAnonymous: !!cfg.isAnonymous,
      isBogon: !!cfg.isBogon,
      isICloudRelay: !!cfg.isICloudRelay,
      callingCode: cfg.callingCode as string || null,
      asnType: cfg.asnType as string || null,
      asnName: cfg.asnName as string || null,
    };
  }

  // Direct localStorage check as fallback
  try {
    const cached = localStorage.getItem(LS_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.pixelId && parsed._ts && (Date.now() - parsed._ts) < CACHE_TTL) {
        return {
          ...EMPTY_CONFIG,
          pixelId: parsed.pixelId,
          ga4MeasurementId: parsed.ga4MeasurementId || null,
          clientIp: parsed.clientIp || null,
          clientIpV6: parsed.clientIpV6 || null,
          clientIpV4: parsed.clientIpV4 || null,
          countryCode: parsed.countryCode || null,
          geoCity: parsed.geoCity || null,
          geoRegion: parsed.geoRegion || null,
          geoPostal: parsed.geoPostal || null,
          isVPN: !!parsed.isVPN,
          isProxy: !!parsed.isProxy,
          isTor: !!parsed.isTor,
          isHosting: !!parsed.isHosting,
          isAbuser: !!parsed.isAbuser,
          isAnonymous: !!parsed.isAnonymous,
          isBogon: !!parsed.isBogon,
          isICloudRelay: !!parsed.isICloudRelay,
          callingCode: parsed.callingCode || null,
          asnType: parsed.asnType || null,
          asnName: parsed.asnName || null,
        };
      }
    }
  } catch { /* silent */ }

  return null;
}

function cacheConfigToLocalStorage(config: PixelConfig) {
  try {
    localStorage.setItem(LS_CACHE_KEY, JSON.stringify({
      ...config,
      _ts: Date.now(),
    }));
  } catch { /* silent */ }
}

// Background refresh to keep cache fresh without blocking initialization
function fetchFreshConfigInBackground() {
  supabase.functions.invoke('get-pixel-config').then(({ data, error }) => {
    if (error || !data?.pixelId) return;
    const freshConfig: PixelConfig = {
      ...EMPTY_CONFIG,
      pixelId: data.pixelId || null,
      ga4MeasurementId: data.ga4MeasurementId || null,
      clientIp: data.clientIp || null,
      clientIpV6: data.clientIpV6 || null,
      clientIpV4: data.clientIpV4 || null,
      countryCode: data.countryCode || null,
      geoCity: data.geoCity || null,
      geoRegion: data.geoRegion || null,
      geoPostal: data.geoPostal || null,
      isVPN: !!data.isVPN,
      isProxy: !!data.isProxy,
      isTor: !!data.isTor,
      isHosting: !!data.isHosting,
      isAbuser: !!data.isAbuser,
      isAnonymous: !!data.isAnonymous,
      isBogon: !!data.isBogon,
      isICloudRelay: !!data.isICloudRelay,
      callingCode: data.callingCode || null,
      asnType: data.asnType || null,
      asnName: data.asnName || null,
    };
    cachedConfig = freshConfig;
    cacheConfigToLocalStorage(freshConfig);
  }).catch(() => { /* silent background refresh */ });
}

async function fetchPixelConfig(): Promise<PixelConfig> {
  if (cachedConfig) return cachedConfig;
  if (fetchPromise) return fetchPromise;

  // Check preloaded/cached config first — eliminates 1-3s delay on return visits
  const preloaded = getPreloadedConfig();
  if (preloaded) {
    cachedConfig = preloaded;
    // Still fetch fresh config in background to keep cache + server IP updated
    fetchFreshConfigInBackground();
    return cachedConfig;
  }

  fetchPromise = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke('get-pixel-config');
      if (error) {
        console.error('Failed to fetch pixel config:', error);
        return EMPTY_CONFIG;
      }
      cachedConfig = {
        ...EMPTY_CONFIG,
        pixelId: data?.pixelId || null,
        ga4MeasurementId: data?.ga4MeasurementId || null,
        clientIp: data?.clientIp || null,
        clientIpV6: data?.clientIpV6 || null,
        clientIpV4: data?.clientIpV4 || null,
        countryCode: data?.countryCode || null,
        geoCity: data?.geoCity || null,
        geoRegion: data?.geoRegion || null,
        geoPostal: data?.geoPostal || null,
        isVPN: !!data?.isVPN,
        isProxy: !!data?.isProxy,
        isTor: !!data?.isTor,
        isHosting: !!data?.isHosting,
        isAbuser: !!data?.isAbuser,
        isAnonymous: !!data?.isAnonymous,
        isBogon: !!data?.isBogon,
        isICloudRelay: !!data?.isICloudRelay,
        callingCode: data?.callingCode || null,
        asnType: data?.asnType || null,
        asnName: data?.asnName || null,
      };
      // Cache to localStorage for next page load (instant init on return visits)
      if (cachedConfig.pixelId) {
        cacheConfigToLocalStorage(cachedConfig);
      }
      return cachedConfig;
    } catch (err) {
      console.error('Error fetching pixel config:', err);
      return EMPTY_CONFIG;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

export function usePixelConfig() {
  const [config, setConfig] = useState<PixelConfig>(
    cachedConfig || getPreloadedConfig() || EMPTY_CONFIG
  );
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    fetchPixelConfig().then(setConfig);
  }, []);

  return config;
}
